import { useQuery } from '@tanstack/vue-query'
import { fetchSiteSupport } from '~/api/site'

/**
 * 在线客服入口（GET /site/support）。
 *
 * - 链接由服务端按后台「站点设置 → 客服配置」拼好：已登录带玩家 ID 与昵称，游客不带。
 *   前端只打开，**不拼接、不改写**。
 * - 未开通（enabled=false）或请求失败：`supportEnabled=false`，各处客服按钮隐藏。
 * - 查询键带登录身份与界面语言：登录 / 退出 / 换号、切换语言后重取（链接里的身份与客服页语言
 *   跟着变；后端按请求的 lang 给链接带上厂商的语言参数）。
 * - 只在客户端取：服务端渲染与水合阶段按「未开通」处理（按钮不渲染），挂载后再出现，
 *   不会有水合不一致。
 * - 必须提前取好、点击时同步 `window.open`：先请求再打开会被 iOS Safari 当成弹窗拦掉。
 */
export function useSupport() {
  const userStore = useUserStore()
  const { locale } = useI18n()

  const mounted = ref(false)
  onMounted(() => {
    mounted.value = true
  })

  const { data } = useQuery({
    queryKey: computed(
      () =>
        [
          'site',
          'support',
          userStore.isLoggedIn ? userStore.user.uid : 'guest',
          String(locale.value),
        ] as const,
    ),
    queryFn: fetchSiteSupport,
    enabled: () => mounted.value,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  /** 可打开的客服链接；未开通 / 取不到时为空串 */
  const supportUrl = computed(() =>
    data.value?.enabled && data.value.chat_url ? data.value.chat_url : '',
  )
  const supportEnabled = computed(() => supportUrl.value !== '')

  /**
   * 新窗口打开客服页。
   *
   * 不在 features 里写 noopener：那样 window.open 恒返回 null，分不清「被拦了」与「打开了」；
   * 改为打开后手动切断 opener（同样不让客服页拿到本页的 window）。被拦截时退回当前页跳转。
   */
  function openSupport() {
    const url = supportUrl.value
    if (!url) return
    const win = window.open(url, '_blank')
    if (win) {
      win.opener = null
    } else {
      window.location.href = url
    }
  }

  return { supportEnabled, openSupport }
}
